import React, { useState } from 'react';

const GAME_FILTERS = [
  { key: 'all', label: 'All Games' },
  { key: 'roulette', label: 'Roulette' },
  { key: 'slots', label: 'Slots' },
  { key: 'blackjack', label: 'Blackjack' },
  { key: 'poker', label: 'Texas Hold\'em' },
  { key: 'mines', label: 'Mines' },
  { key: 'keno', label: 'Keno' },
  { key: 'sportsbet', label: 'Sportsbook' },
];

function getTimeFilters(seasonConfig) {
  const filters = [{ id: 'all', label: 'All Time' }];
  const seasons = seasonConfig?.seasons || {};
  const currentSeason = seasonConfig?.currentSeason || 1;
  for (const sNum of Object.keys(seasons).sort((a, b) => parseInt(b) - parseInt(a))) {
    const s = parseInt(sNum);
    filters.push({
      id: `season_${s}`,
      label: s === currentSeason ? 'This Season' : `Season ${s}`,
    });
  }
  filters.push(
    { id: 'month', label: 'Past Month' },
    { id: 'week', label: 'Past Week' },
    { id: 'day', label: 'Today' },
  );
  return filters;
}

function filterTransactions(transactions, timeFilter, seasonConfig) {
  if (timeFilter === 'all') return transactions;
  const now = Date.now();
  if (timeFilter === 'day') {
    const cutoff = 24 * 60 * 60 * 1000;
    return transactions.filter(tx => {
      const ts = tx.timestamp?.toMillis ? tx.timestamp.toMillis() : tx.timestamp;
      return ts && (now - ts) <= cutoff;
    });
  }
  if (timeFilter === 'week') {
    const cutoff = 7 * 24 * 60 * 60 * 1000;
    return transactions.filter(tx => {
      const ts = tx.timestamp?.toMillis ? tx.timestamp.toMillis() : tx.timestamp;
      return ts && (now - ts) <= cutoff;
    });
  }
  if (timeFilter === 'month') {
    const cutoff = 30 * 24 * 60 * 60 * 1000;
    return transactions.filter(tx => {
      const ts = tx.timestamp?.toMillis ? tx.timestamp.toMillis() : tx.timestamp;
      return ts && (now - ts) <= cutoff;
    });
  }
  if (timeFilter.startsWith('season_')) {
    const seasonNum = parseInt(timeFilter.replace('season_', ''));
    return transactions.filter(tx => tx.season === seasonNum);
  }
  return transactions;
}

// Normalize game names so bonus-buy variants count under their parent game
function txGame(tx) {
  const g = tx.game || '';
  if (g === 'slots-bonus-buy') return 'slots';
  return g;
}

function computePlayerStatsFromTx(transactions, gameFilter) {
  const filtered = gameFilter === 'all' ? transactions : transactions.filter(tx => txGame(tx) === gameFilter);
  const net = filtered.reduce((sum, tx) => sum + (tx.normalizedNet || 0), 0);
  const wagered = filtered.reduce((sum, tx) => sum + (tx.normalizedWagered || 0), 0);
  const bestWin = filtered.reduce((best, tx) => Math.max(best, tx.normalizedNet || 0), 0);
  const worstLoss = filtered.reduce((worst, tx) => Math.min(worst, tx.normalizedNet || 0), 0);
  return { net, wagered, bestWin, worstLoss };
}

function CasinoStatsContent({ casinoStatsAll, seasonConfig }) {
  const [gameFilter, setGameFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');

  const timeFilters = getTimeFilters(seasonConfig);

  // Always compute from transactions — aggregate casinoStats docs are reset each season
  const playerData = casinoStatsAll.map(e => {
    const filteredTx = filterTransactions(e.transactions || [], timeFilter, seasonConfig);
    const computed = computePlayerStatsFromTx(filteredTx, gameFilter);
    return { ...e, computedNet: computed.net, computedWagered: computed.wagered, computedBestWin: computed.bestWin, computedWorstLoss: computed.worstLoss };
  });

  const totalWagered = playerData.reduce((sum, e) => sum + e.computedWagered, 0);

  const bestWin = playerData.reduce(
    (best, e) => e.computedBestWin > best.val ? { val: e.computedBestWin, name: e.player.name } : best,
    { val: 0, name: '' }
  );

  const worstLoss = playerData.reduce(
    (worst, e) => e.computedWorstLoss < worst.val ? { val: e.computedWorstLoss, name: e.player.name } : worst,
    { val: 0, name: '' }
  );

  // Compute multiplier/blackjack stats from transactions (aggregate docs get reset each season)
  const bestMultiplier = (gameFilter === 'all' || gameFilter === 'slots') ? playerData.reduce((best, e) => {
    const slotsTx = (filterTransactions(e.transactions || [], timeFilter, seasonConfig)).filter(tx => txGame(tx) === 'slots');
    const m = slotsTx.reduce((max, tx) => Math.max(max, tx.multiplier || 0), 0);
    return m > best.val ? { val: m, name: e.player.name } : best;
  }, { val: 0, name: '' }) : null;
  const mostBlackjacks = (gameFilter === 'all' || gameFilter === 'blackjack') ? playerData.reduce((best, e) => {
    const bjTx = (filterTransactions(e.transactions || [], timeFilter, seasonConfig)).filter(tx => txGame(tx) === 'blackjack' && tx.isBlackjack);
    // Fall back to aggregate stats for historical transactions that lack isBlackjack field
    const bjCount = bjTx.length > 0 ? bjTx.length : (timeFilter === 'all' ? (e.stats?.blackjack?.blackjacks || 0) : 0);
    return bjCount > best.val ? { val: bjCount, name: e.player.name } : best;
  }, { val: 0, name: '' }) : null;
  const bestMinesMultiplier = (gameFilter === 'all' || gameFilter === 'mines') ? playerData.reduce((best, e) => {
    const minesTx = (filterTransactions(e.transactions || [], timeFilter, seasonConfig)).filter(tx => txGame(tx) === 'mines');
    const m = minesTx.reduce((max, tx) => Math.max(max, tx.multiplier || 0), 0);
    return m > best.val ? { val: m, name: e.player.name } : best;
  }, { val: 0, name: '' }) : null;
  const bestKenoMultiplier = (gameFilter === 'all' || gameFilter === 'keno') ? playerData.reduce((best, e) => {
    const kenoTx = (filterTransactions(e.transactions || [], timeFilter, seasonConfig)).filter(tx => txGame(tx) === 'keno');
    const m = kenoTx.reduce((max, tx) => Math.max(max, tx.multiplier || 0), 0);
    return m > best.val ? { val: m, name: e.player.name } : best;
  }, { val: 0, name: '' }) : null;

  const mostUp = [...playerData]
    .filter(e => e.computedNet > 0)
    .sort((a, b) => b.computedNet - a.computedNet)
    .slice(0, 10);
  const mostDown = [...playerData]
    .filter(e => e.computedNet < 0)
    .sort((a, b) => a.computedNet - b.computedNet)
    .slice(0, 10);

  // Mines leaderboards
  const minesMultiLeaderboard = (gameFilter === 'all' || gameFilter === 'mines') ? playerData.map(e => {
    const minesTx = (filterTransactions(e.transactions || [], timeFilter, seasonConfig)).filter(tx => txGame(tx) === 'mines');
    const bestMult = minesTx.reduce((max, tx) => Math.max(max, tx.multiplier || 0), 0);
    return { name: e.player.name, val: bestMult };
  }).filter(e => e.val > 0).sort((a, b) => b.val - a.val).slice(0, 10) : [];

  const minesPlaysLeaderboard = (gameFilter === 'all' || gameFilter === 'mines') ? playerData.map(e => {
    const minesTx = (filterTransactions(e.transactions || [], timeFilter, seasonConfig)).filter(tx => txGame(tx) === 'mines');
    return { name: e.player.name, val: minesTx.length };
  }).filter(e => e.val > 0).sort((a, b) => b.val - a.val).slice(0, 10) : [];

  // Keno leaderboards
  const kenoMultiLeaderboard = (gameFilter === 'all' || gameFilter === 'keno') ? playerData.map(e => {
    const kenoTx = (filterTransactions(e.transactions || [], timeFilter, seasonConfig)).filter(tx => txGame(tx) === 'keno');
    const bestMult = kenoTx.reduce((max, tx) => Math.max(max, tx.multiplier || 0), 0);
    return { name: e.player.name, val: bestMult };
  }).filter(e => e.val > 0).sort((a, b) => b.val - a.val).slice(0, 10) : [];

  const kenoPlaysLeaderboard = (gameFilter === 'all' || gameFilter === 'keno') ? playerData.map(e => {
    const kenoTx = (filterTransactions(e.transactions || [], timeFilter, seasonConfig)).filter(tx => txGame(tx) === 'keno');
    return { name: e.player.name, val: kenoTx.length };
  }).filter(e => e.val > 0).sort((a, b) => b.val - a.val).slice(0, 10) : [];

  const panelStyle = {
    background: 'var(--bg-card)',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  };
  const panelHeader = (icon, text) => (
    <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ fontSize: '18px' }}>{icon}</span>
      <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>{text}</span>
    </div>
  );
  const panelBody = { padding: '20px' };

  const rankedRow = (rank, name, value, valueColor, isLast = false) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      padding: '12px 0',
      borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
    }}>
      <span style={{ width: '28px', textAlign: 'center', fontSize: '15px', fontWeight: '800', color: rank <= 3 ? '#FBBF24' : 'var(--text-dim)' }}>
        {rank}
      </span>
      <div style={{ flex: 1, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>{name}</div>
      <span style={{ fontSize: '18px', fontWeight: '800', color: valueColor, flexShrink: 0 }}>{value}</span>
    </div>
  );

  return (
    <>
      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {timeFilters.map(f => (
          <button key={f.id} onClick={() => setTimeFilter(f.id)} style={{
            padding: '5px 14px',
            background: timeFilter === f.id ? 'rgba(232, 56, 74, 0.15)' : 'transparent',
            border: `1px solid ${timeFilter === f.id ? 'var(--red)' : 'var(--border)'}`,
            borderRadius: '6px',
            color: timeFilter === f.id ? 'var(--red)' : 'var(--text-dim)',
            fontWeight: timeFilter === f.id ? '700' : '500',
            fontSize: '12px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}>
            {f.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {GAME_FILTERS.map(f => (
          <button key={f.key} onClick={() => setGameFilter(f.key)} style={{
            padding: '5px 14px',
            background: gameFilter === f.key ? 'rgba(232, 56, 74, 0.15)' : 'transparent',
            border: `1px solid ${gameFilter === f.key ? 'var(--red)' : 'var(--border)'}`,
            borderRadius: '6px',
            color: gameFilter === f.key ? 'var(--red)' : 'var(--text-dim)',
            fontWeight: gameFilter === f.key ? '700' : '500',
            fontSize: '12px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Overview Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        marginBottom: '24px',
      }}>
        {[
          { label: 'Total Wagered', value: parseFloat(totalWagered.toFixed(2)).toLocaleString(), color: '#FBBF24' },
          { label: 'Biggest Win', value: bestWin.val > 0 ? parseFloat(bestWin.val.toFixed(2)).toLocaleString() : 'N/A', sub: bestWin.name, color: '#4CAF50' },
          { label: 'Biggest Loss', value: worstLoss.val < 0 ? parseFloat(Math.abs(worstLoss.val).toFixed(2)).toLocaleString() : 'N/A', sub: worstLoss.name, color: '#f44336' },
          ...(bestMultiplier ? [{ label: 'Best Slots Multi', value: bestMultiplier.val > 0 ? `${bestMultiplier.val}x` : 'N/A', sub: bestMultiplier.name, color: '#9C27B0' }] : []),
          ...(mostBlackjacks ? [{ label: 'Most Blackjacks', value: mostBlackjacks.val > 0 ? `${mostBlackjacks.val}` : 'N/A', sub: mostBlackjacks.name, color: '#22D3EE' }] : []),
          ...(bestMinesMultiplier ? [{ label: 'Best Mines Multi', value: bestMinesMultiplier.val > 0 ? `${bestMinesMultiplier.val}x` : 'N/A', sub: bestMinesMultiplier.name, color: '#FF9800' }] : []),
          ...(bestKenoMultiplier ? [{ label: 'Best Keno Multi', value: bestKenoMultiplier.val > 0 ? `${bestKenoMultiplier.val}x` : 'N/A', sub: bestKenoMultiplier.name, color: '#F59E0B' }] : []),
        ].map((stat, i) => (
          <div key={i} style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderLeft: `3px solid ${stat.color}`,
            borderRadius: '8px',
            padding: '16px 20px',
          }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{stat.label}</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', fontFamily: "'Orbitron', sans-serif" }}>{stat.value}</div>
            {stat.sub && <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>{stat.sub}</div>}
          </div>
        ))}
      </div>

      {/* Players Up / Down */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div style={panelStyle}>
          {panelHeader('📈', 'Players Up Most')}
          <div style={panelBody}>
            {mostUp.length > 0 ? mostUp.map((e, i) => (
              rankedRow(i + 1, e.player.name, `+${parseFloat(e.computedNet.toFixed(2)).toLocaleString()}`, '#4ADE80', i === mostUp.length - 1)
            )) : (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>No players in profit yet.</div>
            )}
          </div>
        </div>

        <div style={panelStyle}>
          {panelHeader('📉', 'Players Down Most')}
          <div style={panelBody}>
            {mostDown.length > 0 ? mostDown.map((e, i) => (
              rankedRow(i + 1, e.player.name, `${parseFloat(e.computedNet.toFixed(2)).toLocaleString()}`, '#E84855', i === mostDown.length - 1)
            )) : (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>No players in the red yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Mines Leaderboards */}
      {(gameFilter === 'all' || gameFilter === 'mines') && (minesMultiLeaderboard.length > 0 || minesPlaysLeaderboard.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          {minesMultiLeaderboard.length > 0 && (
            <div style={panelStyle}>
              {panelHeader('💎', 'Highest Mines Multi')}
              <div style={panelBody}>
                {minesMultiLeaderboard.map((e, i) => (
                  rankedRow(i + 1, e.name, `${e.val}x`, '#FF9800', i === minesMultiLeaderboard.length - 1)
                ))}
              </div>
            </div>
          )}
          {minesPlaysLeaderboard.length > 0 && (
            <div style={panelStyle}>
              {panelHeader('💣', 'Most Mines Played')}
              <div style={panelBody}>
                {minesPlaysLeaderboard.map((e, i) => (
                  rankedRow(i + 1, e.name, `${e.val}`, '#FF9800', i === minesPlaysLeaderboard.length - 1)
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Keno Leaderboards */}
      {(gameFilter === 'all' || gameFilter === 'keno') && (kenoMultiLeaderboard.length > 0 || kenoPlaysLeaderboard.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          {kenoMultiLeaderboard.length > 0 && (
            <div style={panelStyle}>
              {panelHeader('🎱', 'Highest Keno Multi')}
              <div style={panelBody}>
                {kenoMultiLeaderboard.map((e, i) => (
                  rankedRow(i + 1, e.name, `${e.val}x`, '#F59E0B', i === kenoMultiLeaderboard.length - 1)
                ))}
              </div>
            </div>
          )}
          {kenoPlaysLeaderboard.length > 0 && (
            <div style={panelStyle}>
              {panelHeader('🎱', 'Most Keno Played')}
              <div style={panelBody}>
                {kenoPlaysLeaderboard.map((e, i) => (
                  rankedRow(i + 1, e.name, `${e.val}`, '#F59E0B', i === kenoPlaysLeaderboard.length - 1)
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default CasinoStatsContent;
